import React, { useState, useEffect, useContext, useRef } from 'react'
import ShowMoreText from 'react-show-more-text'
import { XL_SIZE } from '../../utils/constants'
import { useParams } from 'react-router'
import {
  IPTV_CHANNEL_PLAYLIST,
  GET_ACTIVE_SUBSCRIPTIONS,
  IPTV_CHANNEL_FILTER,
} from '../../utils/apiUrls'
import useAxios from 'axios-hooks'
import {
  getAdUrl,
  getAudioUrl,
  getImage,
  getTrailerUrl,
  getVideoThumbnail,
} from '../../utils/helper'
import { AppContext } from '../../context/app'
import { intersectionWith, isEmpty, isEqual } from 'lodash'
import { SubscriptionContext } from '../../context/subscription'
import moment from 'moment'
import { useInterval } from '../../hooks/useInterval'
import { useTranslation } from 'react-i18next'
import ChannelPlayer from '../player/channelPlayer'
import ReviewsListing from '../../components/reviewRating'
import CommentListing from '../../components/comments'
import ChannelBottom from '../channel/channelBottom'
import { withTheme } from 'theming'
import IptvPlaylistEPG from './iptvPlaylistEPG'
import CustomImage from '../../components/customImage/customImage'
import LockIcon from '../../assets/lock-icon.svg'

const IptvPlaylist = ({ theme }) => {
  const channelId = useParams().channelId
  const { t } = useTranslation()
  const { isLogin, setOpenLoginModal, setModalType, accountObj } = useContext(AppContext)
  const {
    activeSubscription,
    setOpenSubscriptionModal,
    setSubscriptions,
    setRedirectionUrl,
    setContentPartners,
  } = useContext(SubscriptionContext)

  const [detailObj, setDetailObj] = useState({})
  const [showMore, setShowMore] = useState(false)
  const [isChannelSubscribed, setIsChannelSubscribed] = useState(false)
  const [currentPlayingContent, setCurrentPlayingContent] = useState({})
  const [videoSource, setVideoSource] = useState(null)
  const [videoURLError, setVideoURLError] = useState('')
  const [iptvLoader, setIptvLoader] = useState(false)
  const [isXL, setIsXL] = useState(window.innerWidth >= XL_SIZE)

  const playlistRef = useRef([])
  const playlistSignatureRef = useRef('')
  const currentProgramRef = useRef(null)
  const loopStateRef = useRef({
    active: false,
    broken: false,
    sequence: [],
    sequenceSignature: '',
    index: 0,
    startedAtMs: 0,
  })

  let isRatingOnly = accountObj?.od_resource_allowances?.rating_review === 'RATING'

  const [{ data: channelDetails, loading: channelDetailsLoading }, channelDetailsCall] = useAxios(
    {
      url: IPTV_CHANNEL_FILTER,
      method: 'post',
    },
    {
      manual: true,
    },
  )

  const [{ loading: activeSubLoading }, activeSubCall] = useAxios(
    {
      url: GET_ACTIVE_SUBSCRIPTIONS,
      method: 'get',
    },
    { manual: true },
  )

  const [, playlistPollingCall] = useAxios(
    {
      url: IPTV_CHANNEL_PLAYLIST,
      method: 'get',
    },
    { manual: true },
  )

  useEffect(() => {
    setIptvLoader(channelDetailsLoading || activeSubLoading)
  }, [channelDetailsLoading, activeSubLoading])

  useEffect(() => {
    const handleResize = () => {
      setIsXL(window.innerWidth >= XL_SIZE)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setIsChannelSubscribed(true)
    setShowMore(false)
    setVideoSource(null)
    setVideoURLError('')
    setCurrentPlayingContent({})

    playlistRef.current = []
    playlistSignatureRef.current = ''
    currentProgramRef.current = null

    channelDetailsCall({
      data: {
        filters: { channel_ids: [channelId] },
        page: '1',
      },
    })
  }, [channelId])

  const getPlaylistStreamUrl = (program) => {
    if (program?.is_hls_url && program?.hls_link) {
      return program.hls_link
    }

    if (!accountObj?.cf_domain_name || !program?.asset_id) {
      return ''
    }

    if (program?.is_trailer) {
      return getTrailerUrl(accountObj?.cf_domain_name, program.asset_id, 480)
    }

    if (program?.is_advertisement) {
      return getAdUrl(accountObj?.cf_domain_name, program.asset_id, 480)
    }

    if (program.file_metadata?.file_type.includes('audio')) {
      return getAudioUrl(accountObj?.cf_domain_name, program.asset_id)
    }

    const baseUrl = /^https?:\/\//i.test(accountObj.cf_domain_name)
      ? accountObj.cf_domain_name
      : `https://${accountObj.cf_domain_name}`

    return `${baseUrl.replace(/\/$/, '')}/assets/${program.asset_id}/hls/master.m3u8`
  }

  const normalizePlaylistData = (playlist = []) => {
    return (playlist || [])
      .map((program) => {
        const startMoment = moment.utc(program?.start_time)
        let endMoment = moment.utc(program?.end_time)

        if (!startMoment.isValid() || !endMoment.isValid()) return null
        if (endMoment.isSameOrBefore(startMoment)) {
          endMoment = endMoment.add(1, 'day')
        }

        return {
          ...program,
          title: program?.title || program?.file_name || 'Untitled Program',
          description: program?.description || '',
          playUrl: getPlaylistStreamUrl(program),
          _startMoment: startMoment,
          _endMoment: endMoment,
          _programKey: `${program?.asset_id || program?.file_name}-${program?.start_time}`,
          _loopDurationMs: Math.max(
            1000,
            Math.round(
              ((Number(program?.duration) > 0
                ? Number(program?.duration)
                : Math.max(1, endMoment.diff(startMoment, 'seconds', true))) || 1) * 1000,
            ),
          ),
        }
      })
      .filter(Boolean)
      .sort((first, second) => first._startMoment.valueOf() - second._startMoment.valueOf())
  }

  const getLoopSignature = (sequence = []) => {
    return sequence.map((item) => `${item._programKey}-${item._loopDurationMs}`).join('|')
  }

  const getLatestCompletedExactEndMs = (playlist = [], nowUtc = moment.utc()) => {
    const nowMs = nowUtc.valueOf()
    return playlist.reduce((latestEndMs, item) => {
      if (!item?.has_exact_time) return latestEndMs

      const exactEndMs = item?._endMoment?.valueOf() || 0
      if (exactEndMs > 0 && nowMs >= exactEndMs) {
        return Math.max(latestEndMs, exactEndMs)
      }

      return latestEndMs
    }, 0)
  }

  const getLoopSequence = (playlist = [], nowUtc = moment.utc(), brokenBeforeMs = 0) => {
    const nowMs = nowUtc.valueOf()

    return playlist.filter((item) => {
      if (!item?.is_looped) return false

      const loopStartMs = item?._startMoment?.valueOf() || 0
      const loopEndMs = item?._endMoment?.valueOf() || 0

      // Loops that ended before the latest completed exact-time boundary are considered broken.
      if (brokenBeforeMs > 0 && loopEndMs > 0 && loopEndMs <= brokenBeforeMs) return false

      // Only loops that have started should participate in loop playback selection.
      return loopStartMs > 0 && nowMs >= loopStartMs
    })
  }

  const syncLoopState = (loopSequence = [], preferredProgramKey = null) => {
    const state = loopStateRef.current
    const signature = getLoopSignature(loopSequence)

    if (!signature) {
      loopStateRef.current = {
        active: false,
        broken: false,
        sequence: [],
        sequenceSignature: '',
        index: 0,
        startedAtMs: 0,
      }
      return
    }

    if (!state.active) {
      const preferredIndex = loopSequence.findIndex(
        (item) => item._programKey === preferredProgramKey,
      )
      loopStateRef.current = {
        active: true,
        broken: false,
        sequence: loopSequence,
        sequenceSignature: signature,
        index: preferredIndex >= 0 ? preferredIndex : 0,
        startedAtMs: moment.utc().valueOf(),
      }
      return
    }

    if (state.sequenceSignature !== signature) {
      const currentKey = state.sequence[state.index]?._programKey
      const nextIndex = loopSequence.findIndex(
        (item) => item._programKey === currentKey || item._programKey === preferredProgramKey,
      )
      loopStateRef.current = {
        active: true,
        broken: false,
        sequence: loopSequence,
        sequenceSignature: signature,
        index: nextIndex >= 0 ? nextIndex : 0,
        startedAtMs: moment.utc().valueOf(),
      }
    }
  }

  const getActiveLoopProgram = (nowMs) => {
    const state = loopStateRef.current
    if (!state.active || isEmpty(state.sequence)) return null

    let guard = 0
    while (guard < state.sequence.length * 2) {
      const currentProgram = state.sequence[state.index]
      const durationMs = Math.max(1000, currentProgram?._loopDurationMs || 1000)

      if (nowMs - state.startedAtMs < durationMs) {
        return currentProgram
      }

      state.startedAtMs += durationMs
      state.index = (state.index + 1) % state.sequence.length
      guard += 1
    }

    return state.sequence[state.index] || null
  }

  const initializeLoopStateFromTimeline = (
    loopSequence = [],
    nowMs,
    preferredProgramKey = null,
  ) => {
    if (isEmpty(loopSequence)) return

    if (preferredProgramKey) {
      syncLoopState(loopSequence, preferredProgramKey)
      return
    }

    const totalDurationMs = loopSequence.reduce(
      (sum, item) => sum + Math.max(1000, item?._loopDurationMs || 1000),
      0,
    )
    if (totalDurationMs <= 0) {
      syncLoopState(loopSequence)
      return
    }

    const firstStartMs = loopSequence[0]?._startMoment?.valueOf() || nowMs
    const elapsedSinceStart = Math.max(0, nowMs - firstStartMs)
    let offsetInCycle = elapsedSinceStart % totalDurationMs

    let index = 0
    while (index < loopSequence.length) {
      const durationMs = Math.max(1000, loopSequence[index]?._loopDurationMs || 1000)
      if (offsetInCycle < durationMs) break
      offsetInCycle -= durationMs
      index += 1
    }

    if (index >= loopSequence.length) {
      index = 0
      offsetInCycle = 0
    }

    loopStateRef.current = {
      active: true,
      broken: false,
      sequence: loopSequence,
      sequenceSignature: getLoopSignature(loopSequence),
      index,
      startedAtMs: nowMs - offsetInCycle,
    }
  }

  const checkPlanExist = (contentActiveSubscription, userSubscription) => {
    return !isEmpty(intersectionWith(contentActiveSubscription, userSubscription, isEqual))
  }

  const emptySubcheck = async (channelSub) => {
    const subRes = await activeSubCall()
    const activeSub = subRes.data.data
      .filter((item) => item.status)
      .map((item) => item.subscription)

    return !isEmpty(intersectionWith(channelSub, activeSub, isEqual))
  }

  const checkRestriction = async (channelDetail, openModal = false) => {
    if (!channelDetail?.is_restricted) {
      setIsChannelSubscribed(true)
      return true
    }

    if (!isLogin) {
      if (openModal) {
        setModalType('login')
        setOpenLoginModal(true)
      }
      setIsChannelSubscribed(false)
      return false
    }

    const channelSub = [
      ...(channelDetail?.active_subscription || []).map((item) => item._id),
      ...(channelDetail?.active_ppv || []).map((item) => item._id),
    ]

    const planExist = isEmpty(activeSubscription)
      ? await emptySubcheck(channelSub)
      : checkPlanExist(channelSub, activeSubscription)

    if (!planExist) {
      setIsChannelSubscribed(false)
      if (openModal) {
        setRedirectionUrl(window.location.pathname)
        setContentPartners(channelDetail?.owner ? channelDetail.owner : [])
        setSubscriptions(channelSub)
        setOpenSubscriptionModal(true)
      }
      return false
    }

    setIsChannelSubscribed(true)
    return true
  }

  const getPlaylistSignature = (playlist = []) => {
    return playlist
      .map(
        (item) =>
          `${item.asset_id || item.file_name}-${item.start_time}-${item.end_time}-${Boolean(
            item.has_exact_time,
          )}-${Boolean(item.is_looped)}`,
      )
      .join('|')
  }

  const pickActiveProgram = () => {
    const playlist = playlistRef.current
    if (isEmpty(playlist) || !isChannelSubscribed) return null

    const nowUtc = moment.utc()
    const latestCompletedExactEndMs = getLatestCompletedExactEndMs(playlist, nowUtc)
    const loopSequence = getLoopSequence(playlist, nowUtc, latestCompletedExactEndMs)
    const normalProgram = playlist
      .filter(
        (item) =>
          !item?.has_exact_time &&
          !item?.is_looped &&
          nowUtc.isSameOrAfter(item._startMoment) &&
          nowUtc.isBefore(item._endMoment),
      )
      .pop()
    const isInNextDayWindow = nowUtc.isSameOrAfter(moment.utc().startOf('day').add(1, 'day'))

    const exactProgram = playlist.find(
      (item) =>
        item?.has_exact_time &&
        nowUtc.isSameOrAfter(item._startMoment) &&
        nowUtc.isBefore(item._endMoment),
    )
    if (exactProgram) {
      loopStateRef.current = { ...loopStateRef.current, active: false, broken: false }
      return exactProgram
    }

    if (currentProgramRef.current?.is_looped) {
      const currentLoopEndMs = currentProgramRef.current?._endMoment?.valueOf() || 0
      if (
        latestCompletedExactEndMs > 0 &&
        currentLoopEndMs > 0 &&
        currentLoopEndMs <= latestCompletedExactEndMs
      ) {
        loopStateRef.current = { ...loopStateRef.current, active: false, broken: true }
      }
    }

    if (!isEmpty(loopSequence)) {
      if (!loopStateRef.current.active) {
        const firstLoopStartMs = loopSequence[0]?._startMoment?.valueOf() || 0
        const shouldResumeLoop =
          currentProgramRef.current?.is_looped || nowUtc.valueOf() >= firstLoopStartMs

        if (shouldResumeLoop) {
          initializeLoopStateFromTimeline(
            loopSequence,
            nowUtc.valueOf(),
            currentProgramRef.current?.is_looped ? currentProgramRef.current?._programKey : null,
          )
        }
      }

      if (loopStateRef.current.active) {
        syncLoopState(loopSequence)
        const activeLoopProgram = getActiveLoopProgram(nowUtc.valueOf())
        if (activeLoopProgram) {
          if (isInNextDayWindow && normalProgram) {
            loopStateRef.current = { ...loopStateRef.current, active: false }
            return normalProgram
          }
          return activeLoopProgram
        }
        loopStateRef.current.active = false
      }

      if (currentProgramRef.current?.is_looped) {
        syncLoopState(loopSequence, currentProgramRef.current?._programKey)
        const activeLoopProgram = getActiveLoopProgram(nowUtc.valueOf())
        if (activeLoopProgram) {
          if (isInNextDayWindow && normalProgram) {
            loopStateRef.current = { ...loopStateRef.current, active: false }
            return normalProgram
          }
          return activeLoopProgram
        }
      }
    }

    if (normalProgram) {
      loopStateRef.current = { ...loopStateRef.current, active: false }
      return normalProgram
    }

    return null
  }

  const switchProgramIfNeeded = (nextProgram) => {
    if (!nextProgram) {
      if (!isEmpty(currentPlayingContent) || videoSource) {
        currentProgramRef.current = null
        setCurrentPlayingContent({})
        setVideoSource(null)
      }
      return
    }

    if (!nextProgram?.playUrl) {
      currentProgramRef.current = null
      setCurrentPlayingContent({})
      setVideoSource(null)
      setVideoURLError('Unable to play this program. Stream URL is missing.')
      return
    }

    const isSameProgram = currentProgramRef.current?._programKey === nextProgram?._programKey
    if (isSameProgram && videoSource === nextProgram.playUrl) return

    currentProgramRef.current = nextProgram
    setVideoURLError('')
    setCurrentPlayingContent(nextProgram)
    setVideoSource(nextProgram.playUrl)
  }

  const syncPlayback = () => {
    switchProgramIfNeeded(pickActiveProgram())
  }

  const getResumeTime = () => {
    if (isEmpty(currentPlayingContent)) return 0

    const nowMs = moment.utc().valueOf()
    const isCurrentLoopProgram =
      loopStateRef.current?.active &&
      loopStateRef.current?.sequence?.[loopStateRef.current.index]?._programKey ===
        currentPlayingContent?._programKey

    const startMs = isCurrentLoopProgram
      ? loopStateRef.current.startedAtMs
      : currentPlayingContent?._startMoment?.valueOf()

    if (!startMs) return 0

    const elapsedSeconds = Math.floor((nowMs - startMs) / 1000)
    if (elapsedSeconds <= 0) return 0

    const maxDuration =
      Number(currentPlayingContent?.duration) > 0
        ? Math.floor(Number(currentPlayingContent.duration))
        : null

    if (!maxDuration) return elapsedSeconds
    return Math.min(elapsedSeconds, Math.max(0, maxDuration - 1))
  }

  const fetchPlaylist = async () => {
    if (!isChannelSubscribed) return

    try {
      const startTimeUtc = moment().startOf('day').utc().toISOString()
      // Include next-day schedule so looped fallback can hand over as soon as tomorrow's items start.
      const endTimeUtc = moment().startOf('day').add(2, 'day').utc().toISOString()

      const response = await playlistPollingCall({
        params: {
          channel_id: channelId,
          start_time: startTimeUtc,
          end_time: endTimeUtc,
        },
      })
      const result = response?.data

      if (!result?.success || isEmpty(result?.data)) return

      const normalizedPlaylist = normalizePlaylistData(result?.data?.playlist_data || [])

      const nextSignature = getPlaylistSignature(normalizedPlaylist)
      if (playlistSignatureRef.current === nextSignature) return

      playlistSignatureRef.current = nextSignature
      playlistRef.current = normalizedPlaylist
      loopStateRef.current = {
        ...loopStateRef.current,
        broken: false,
      }
      syncPlayback()
    } catch (error) {
      console.error('Playlist fetch failed', error)
    }
  }

  useEffect(() => {
    const syncChannelDetails = async () => {
      if (!channelDetails || !channelDetails.success || isEmpty(channelDetails?.data)) return

      const channelDetail = channelDetails.data[0]
      setDetailObj(channelDetail)

      const isAllowed = await checkRestriction(channelDetail)
      if (!isAllowed) {
        playlistRef.current = []
        playlistSignatureRef.current = ''
        currentProgramRef.current = null
        loopStateRef.current = { ...loopStateRef.current, active: false, broken: false }
        setCurrentPlayingContent({})
        setVideoSource(null)
        return
      }

      await fetchPlaylist()
      syncPlayback()
    }

    syncChannelDetails()
  }, [channelDetails, isLogin, activeSubscription])

  useInterval(() => {
    fetchPlaylist()
  }, 30000)

  useInterval(() => {
    syncPlayback()
  }, 500)

  const menuComponents = [
    {
      title: isRatingOnly ? t('rating') : t('reviews'),
      child: (
        <ReviewsListing
          type={'channel'}
          id={channelId}
          title={detailObj?.title}
          isRefreshed={channelDetailsLoading}
        />
      ),
    },
    {
      title: t('comments'),
      child: <CommentListing type={'channel'} id={channelId} theme={theme} />,
    },
  ]

  return (
    <div className="w-full h-full">
      <div className="flex flex-col xl:flex-row xl:justify-between xl:mt-3 px-0 2xl:max-w-[1630px] xl:max-w-[1297px] 2xl:h-[573px] xl:h-[433px] mx-auto">
        <div className="flex xl:hidden w-full justify-center items-center">
          <CustomImage
            imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
            alt="logo"
            imgClass="h-9 w-[101px]"
            loaderClass="h-9 w-[101px]"
          />
        </div>

        <div className="mb-3 xl:hidden text-center">
          <h3 className="test-xs font-semibold mb-1 text-white">{detailObj?.name}</h3>
        </div>

        <div
          className={`${
            iptvLoader && 'animate-pulse'
          } bg-black rounded-xl w-full xl:w-[769px] 2xl:w-[1018px] mb-3 xl:mb-0 overflow-hidden`}
          style={{ aspectRatio: '1.777' }}
        >
          {videoSource && (
            <ChannelPlayer
              contentInfo={currentPlayingContent}
              contentWatchUrl={videoSource}
              resumeTime={getResumeTime()}
              watermarkLogo={detailObj?.logo?.website?.s3_key}
              detailObj={detailObj}
            />
          )}

          {videoURLError && !videoSource ? (
            <div className="w-full h-full flex justify-center items-center">
              <p
                style={{ color: '#cecece' }}
                className="text-xs md:text-base xl:text-lg text-center font-semibold"
              >
                {videoURLError}
              </p>
            </div>
          ) : null}

          {!videoSource ? (
            !isChannelSubscribed ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="flex px-5 md:px-0">
                  <img
                    src={LockIcon}
                    style={{ width: '18.5px', height: '24.6px' }}
                    alt="LockIcon"
                  />
                  <p
                    style={{ color: '#cecece' }}
                    className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                  >
                    {t('this_channel_not_included')}
                  </p>
                </div>
                <button
                  style={{ background: '#09e9e9', color: '#000' }}
                  className="w-[70%] text-xs md:text-base xl:text-lg rounded-md xl:rounded-lg mt-4 xl:mt-6 py-1"
                  onClick={() => checkRestriction(detailObj, true)}
                >
                  {t('upgrade_membership_to_watch')}
                </button>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <CustomImage
                  imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
                  imgAlt="channelimg"
                  imgClass="h-6 w-9 md:h-12 md:w-20"
                  loaderClass="h-6 w-9 md:h-12 md:w-20"
                />
                <p
                  style={{ color: '#cecece' }}
                  className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                >
                  {t('no_programs_shown_at_moment')}
                </p>
              </div>
            )
          ) : null}
        </div>

        <div
          className={`${
            iptvLoader && 'animate-pulse'
          } w-full xl:w-[493px] 2xl:w-[561px] flex flex-col px-3 xl:px-0 relative`}
        >
          <div className="hidden w-full xl:flex justify-center items-center">
            <CustomImage
              imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
              alt="logo"
              imgClass="xl:h-[50px] 2xl:h-[71px]"
              loaderClass="xl:h-[50px] 2xl:h-[71px]"
              imgStyle={{ aspectRatio: '1.7857' }}
              loaderStyle={{ aspectRatio: '1.7857' }}
            />
          </div>

          <div className="hidden xl:block text-center mt-1">
            <h3 className="2xl:text-[22px] text-base font-semibold mb-1 text-white">
              {detailObj?.name}
            </h3>
            <p className="2xl:text-xs text-[11px] mt-3 px-4" style={{ color: '#ACACAC' }}>
              {detailObj?.description}
            </p>
          </div>

          <div
            style={{ borderTop: '1px solid #707070' }}
            className="hidden xl:block xl:mt-[14px]"
          />

          {!isEmpty(currentPlayingContent) ? (
            <div className="text-sm uppercase mt-2 xl:mt-3 2xl:mt-4" style={{ color: '#007FFF' }}>
              {t('streaming_now')}
            </div>
          ) : null}

          {!isEmpty(currentPlayingContent) ? (
            <div className="flex gap-4 xl:gap-5 mb-3 xl:mb-4 flex-1 overflow-hidden">
              <div style={{ width: 'calc(100% - 160px)' }} className="flex-1 flex flex-col">
                <p
                  style={{ overflowWrap: 'break-word' }}
                  className="font-bold mb-1 text-sm xl:text-sm 2xl:text-base text-white"
                >
                  {currentPlayingContent?.title}
                </p>
                <div className="mb-3 xl:mb-0 flex-1 overflow-y-auto hidden-scrollbar-class">
                  <p className="text-xs mb-3" style={{ color: '#ACACAC' }}>
                    <ShowMoreText
                      lines={isXL ? 4 : 3}
                      more={<span style={{ color: '#4DA6FF' }}>show more</span>}
                      less={<span style={{ color: '#4DA6FF' }}>show less</span>}
                      anchorClass="text-sm cursor-pointer"
                      className="w-full text-xs"
                      expanded={showMore}
                      onClick={() => setShowMore(!showMore)}
                      truncatedEndingComponent={'... '}
                    >
                      <p className="w-full">{currentPlayingContent?.description}</p>
                    </ShowMoreText>
                  </p>
                </div>
              </div>

              <div className="w-[143px] xl:w-[151px] 2xl:w-48">
                <CustomImage
                  imageUrl={getVideoThumbnail(
                    accountObj?.cf_domain_name,
                    currentPlayingContent?.asset_id,
                  )}
                  alt="ContentImage"
                  imgClass="w-full h-20 xl:h-[85px] 2xl:h-[107px] object-contain bg-black"
                  style={{ border: '0.5px solid #FFFFFF', borderRadius: '2px' }}
                />
              </div>
            </div>
          ) : null}

          <hr className="mb-3 xl:hidden border-gray-700" />

          <div
            style={{ background: theme.c1 }}
            className="xl:absolute bottom-0 flex items-center gap-2 xl:gap-5 text-[10px] xl:text-xs 2xl:text-sm text-white py-1"
          >
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#11171F', border: '1px solid #727272' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">Program</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#141446', border: '1px solid #1F51FF' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">Streaming Now</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#5C1921', border: '1px solid #FF3131' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">LIVE NOW</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#5C3819', border: '1px solid #FF9300' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">Upcoming LIVE</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full 2xl:max-w-[1630px] xl:max-w-[1297px] mx-auto mt-[21px]">
        <IptvPlaylistEPG currentPlayingContent={currentPlayingContent} />
      </div>

      <div className="w-full 2xl:max-w-[88.8%] xl:max-w-[1297px] mx-auto mt-8">
        <ChannelBottom options={menuComponents} />
      </div>
    </div>
  )
}

export default withTheme(IptvPlaylist)
